import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { addMonths, addDays, format, parseISO } from 'date-fns';
import type { StateTaxObligationSummary } from '../../types';
import RecordManualFilingDialog from '../../components/compliance/RecordManualFilingDialog';
import {
  generateTasksNow,
  findTaskForPeriod,
  previewTasks,
  type TaskGenerationPreviewItem,
} from '../../services/complianceApi';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stateTaxClientId: string;
  obligations: StateTaxObligationSummary[];
  taxType: string;
  periodStart: string;
  cadence?: string; // filingFrequencyLabel from obligation, e.g. "Monthly", "Quarterly", "Semi-Annually", "Annual"
  periodLabel?: string;
  /** Client's display name (Dataverse account name) — threaded into RecordManualFilingDialog
   * for the Flow C name-mismatch warning. */
  clientName?: string;
}

/** Map a filing-frequency label to the number of months in that period. */
function cadenceToMonths(cadence: string | undefined): number {
  const c = (cadence ?? '').toLowerCase().replace(/[-\s]/g, '');
  if (c === 'quarterly') return 3;
  if (c === 'semiannual' || c === 'semiannually') return 6;
  if (c === 'annual' || c === 'annually') return 12;
  return 1; // Monthly (default)
}

/**
 * Compute the asOf date to pass to generateTasksNow so that the backend
 * generates exactly the period whose start is periodStart.
 *
 * Backend semantics (CompliancePeriodMath.ComputePeriodEnd):
 *   "most recently completed reporting period whose end <= asOf"
 *
 * So we need asOf to fall WITHIN the period's filing-due window:
 *   periodEnd = periodStart + cadenceMonths - 1 day
 *   asOf = periodEnd + 25 days   (lands in the filing window; FL due offset ~20 days)
 */
function computeAsOf(periodStart: string, cadence: string | undefined): string {
  const months = cadenceToMonths(cadence);
  const start = parseISO(periodStart);
  const periodEnd = addDays(addMonths(start, months), -1);
  const asOf = addDays(periodEnd, 25);
  return format(asOf, 'yyyy-MM-dd');
}

interface PreviewChecklistItem {
  obligationId: string;
  label: string;
}

/**
 * Collapse the preview rows to one entry per distinct obligationId (a single
 * obligation can appear more than once if the preview window spans two periods),
 * joining period labels with ", " so the checklist shows one checkbox per obligation.
 */
function groupByObligation(items: TaskGenerationPreviewItem[]): PreviewChecklistItem[] {
  const order: string[] = [];
  const byId = new Map<string, { obligationDisplayName: string; periodLabels: string[] }>();
  for (const item of items) {
    const existing = byId.get(item.obligationId);
    if (existing) {
      if (!existing.periodLabels.includes(item.periodLabel)) {
        existing.periodLabels.push(item.periodLabel);
      }
    } else {
      byId.set(item.obligationId, { obligationDisplayName: item.obligationDisplayName, periodLabels: [item.periodLabel] });
      order.push(item.obligationId);
    }
  }
  return order.map((obligationId) => {
    const entry = byId.get(obligationId)!;
    return { obligationId, label: `${entry.obligationDisplayName} — ${entry.periodLabels.join(', ')}` };
  });
}

export default function FilingPeriodActionDialog({
  open,
  onOpenChange,
  stateTaxClientId,
  obligations,
  taxType,
  periodStart,
  cadence,
  periodLabel,
  clientName,
}: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [manualOpen, setManualOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processError, setProcessError] = useState<string | null>(null);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());

  // Derived rows are the legacy synthesized obligations — the client hasn't been opted
  // into task processing yet, so there's nothing to preview (see handleProcessAsTask).
  const isDerived = obligations[0]?.source === 'derived';

  // CRITICAL 2 — compute asOf from period end so the backend (and preview) target the
  // correct period. FilingHistoryStrip mounts this dialog unconditionally with
  // open={false} and periodStart='' until a cell is clicked, so guard against an
  // empty periodStart — computeAsOf('') throws (date-fns parseISO/format on an
  // invalid date) and would crash the strip on normal page render.
  const asOf = periodStart ? computeAsOf(periodStart, cadence) : '';

  const previewQuery = useQuery({
    queryKey: ['task-generation-preview', stateTaxClientId, asOf],
    queryFn: () => previewTasks(stateTaxClientId, asOf),
    enabled: open && !isDerived && !!periodStart,
  });

  const items = groupByObligation(previewQuery.data ?? []);
  const checkedIds = items.filter((i) => !excluded.has(i.obligationId)).map((i) => i.obligationId);
  const previewEmpty = previewQuery.isSuccess && items.length === 0;
  const noneChecked = previewQuery.isSuccess && items.length > 0 && checkedIds.length === 0;

  // IMPORTANT 3 — clear stale error when dialog is closed
  useEffect(() => {
    if (!open) setProcessError(null);
  }, [open]);

  // Reset the checklist selection each time the dialog opens.
  useEffect(() => {
    if (open) setExcluded(new Set());
  }, [open]);

  // Auto-dismiss the success banner after 4 seconds
  useEffect(() => {
    if (!savedSuccess) return;
    const timer = setTimeout(() => setSavedSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [savedSuccess]);

  function handleManualSaved() {
    onOpenChange(false);
    setSavedSuccess(true);
  }

  async function handleProcessAsTask() {
    // Derived rows are the legacy synthesized obligations — the client hasn't been
    // opted into task processing yet (opt-in = an explicit/cosmos obligation row).
    // The generator can't see derived rows, so fail fast with guidance instead of
    // a silent no-op.
    if (isDerived) {
      setProcessError(t('clientDetailPage.history.actions.notOptedIn'));
      return;
    }

    setProcessing(true);
    setProcessError(null);
    try {
      // A broken preview must not block task creation — if the preview fetch failed we
      // have no idea which obligations are due, so fall back to generating everything
      // eligible for this period by omitting obligationIds entirely.
      const obligationIdsArg = previewQuery.isError ? undefined : checkedIds;
      await generateTasksNow(stateTaxClientId, asOf, obligationIdsArg);
      const taskId = await findTaskForPeriod(stateTaxClientId, periodStart);
      if (taskId) {
        // CRITICAL 1 — include clientId on success nav
        // programmatic-nav: after create, the new task's page (no element is being followed)
        navigate(`/compliance/tasks/${taskId}?clientId=${encodeURIComponent(stateTaxClientId)}`);
        onOpenChange(false);
      } else {
        // Nothing materialized for this period — stay in the dialog and say so
        // (likely causes: reporting-since after this period, or no schedulable
        // filing frequency on the obligation).
        setProcessError(t('clientDetailPage.history.actions.noTaskGenerated'));
      }
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : t('clientDetailPage.history.actions.unexpectedError'));
    } finally {
      setProcessing(false);
    }
  }

  const heading = periodLabel
    ? `${taxType} — ${periodLabel}`
    : `${taxType} — ${periodStart}`;

  return (
    <>
      <Dialog open={open} onOpenChange={(_e, data) => onOpenChange(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{heading}</DialogTitle>
            <DialogContent>
              <Text block style={{ marginBottom: '8px' }}>
                {t('clientDetailPage.history.actions.uploadRecommended')}
              </Text>
              {!isDerived && (
                <>
                  <Text block weight="semibold" style={{ marginBottom: '4px' }}>
                    {t('clientDetailPage.history.actions.previewTitle')}
                  </Text>
                  {previewQuery.isLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Spinner size="tiny" />
                      <Text>{t('clientDetailPage.history.actions.previewLoading')}</Text>
                    </div>
                  )}
                  {previewQuery.isError && (
                    <Text block style={{ marginBottom: '8px' }}>
                      {t('clientDetailPage.history.actions.unexpectedError')}
                    </Text>
                  )}
                  {previewEmpty && (
                    <Text block style={{ marginBottom: '8px' }}>
                      {t('clientDetailPage.history.actions.noTaskGenerated')}
                    </Text>
                  )}
                  {previewQuery.isSuccess && items.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
                      {items.map((item) => (
                        <Checkbox
                          key={item.obligationId}
                          label={item.label}
                          checked={!excluded.has(item.obligationId)}
                          onChange={(_e, data) => {
                            setExcluded((prev) => {
                              const next = new Set(prev);
                              if (data.checked) {
                                next.delete(item.obligationId);
                              } else {
                                next.add(item.obligationId);
                              }
                              return next;
                            });
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {noneChecked && (
                    <Text block style={{ color: 'red', marginBottom: '8px' }}>
                      {t('clientDetailPage.history.actions.selectAtLeastOne')}
                    </Text>
                  )}
                </>
              )}
              {processError && (
                <Text block style={{ color: 'red', marginBottom: '8px' }}>{processError}</Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="subtle"
                onClick={() => onOpenChange(false)}
              >
                {t('clientDetailPage.history.actions.cancel')}
              </Button>
              <Button
                appearance="secondary"
                onClick={() => setManualOpen(true)}
              >
                {t('clientDetailPage.history.actions.markAsFiled')}
              </Button>
              <Button
                appearance="primary"
                disabled={processing || previewQuery.isLoading || previewEmpty || noneChecked}
                icon={processing ? <Spinner size="tiny" /> : undefined}
                onClick={() => { void handleProcessAsTask(); }}
              >
                {t('clientDetailPage.history.actions.processAsTask')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <RecordManualFilingDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        stateTaxClientId={stateTaxClientId}
        obligations={obligations}
        defaultTaxType={taxType}
        defaultPeriodStart={periodStart}
        onSaved={handleManualSaved}
        clientName={clientName}
      />

      {savedSuccess && (
        <MessageBar intent="success" style={{ marginTop: '8px' }}>
          <MessageBarBody>{t('clientDetailPage.history.actions.filedConfirmation')}</MessageBarBody>
        </MessageBar>
      )}
    </>
  );
}
